import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';

/**
 * C-15 — what a venue still needs before it can trade.
 *
 * Every row is derived from a real read, never from a local "you finished this"
 * flag: a tenant can be seeded, imported or edited from another device, and a
 * remembered flag would keep insisting the venue is ready after someone deleted
 * the last table.
 */
export type StepState = 'done' | 'missing' | 'restricted' | 'error';

export type StepId =
  | 'branch'
  | 'floor'
  | 'area'
  | 'table'
  | 'station'
  | 'item'
  | 'menu'
  | 'map'
  | 'staff';

export type ScreenId =
  | 'settingsBranches'
  | 'settingsSpaces'
  | 'settingsStaff'
  | 'catalogStations'
  | 'catalogItems'
  | 'catalogMenus'
  | 'floorMapEditor';

export interface SetupStep {
  id: StepId;
  state: StepState;
  /** The screen that fixes this gap, named for the operator. */
  screen: ScreenId;
  /** Route to that screen, or null when this actor would only reach /403. */
  to: string | null;
  /** How many the probe found, for the steps where a number reads better. */
  count: number;
  /**
   * The two permission keys this step turns on, named rather than described.
   *
   * "Ask an administrator" is not a request anybody can act on: the person being
   * asked has to know which key to grant, and a venue is normally set up by more
   * than one pair of hands. `view` is what the probe needed to answer at all —
   * without it the step reads `restricted`, which is "unknown", not "missing" —
   * and `fix` is what closes the gap.
   */
  needs: { view: string; fix: string };
}

interface Probe {
  url: string;
  params: Record<string, unknown>;
  /** The permission `GET` on this path enforces — checked in the controller. */
  view: string;
}

/**
 * One request per endpoint, shared by the steps derived from it: `/floors`
 * already carries `_count.mapElements`, so asking whether anything is drawn
 * costs nothing extra, where `GET /floor-map` would download every element of
 * every scene to answer the same yes/no.
 */
const PROBES = {
  branches: { url: '/branches', params: { limit: 1 }, view: 'branches.view' },
  floors: { url: '/floors', params: { limit: 200 }, view: 'floor_map.view' },
  areas: { url: '/areas', params: { limit: 1 }, view: 'floor_map.view' },
  tables: { url: '/tables', params: { limit: 1 }, view: 'tables.view' },
  stations: { url: '/kitchen', params: { limit: 200 }, view: 'kitchen.view' },
  catalog: { url: '/catalog', params: { isActive: true, limit: 1 }, view: 'menu.view' },
  menus: { url: '/menus', params: { limit: 200 }, view: 'menu.view' },
  // `isActive` is an IsBooleanString on the API — a bare boolean would be
  // coerced to true by the global pipe and could never mean "false".
  users: { url: '/users', params: { isActive: 'true', limit: 200 }, view: 'users.view' },
} satisfies Record<string, Probe>;

type ProbeId = keyof typeof PROBES;

interface ProbeResult {
  outcome: 'ok' | 'restricted' | 'error';
  rows: unknown[];
  total: number;
}

interface FloorRow {
  id: string;
  name: string;
  _count?: { areas?: number; mapElements?: number };
}

interface MenuRow {
  isActive?: boolean;
  _count?: { items?: number };
}

interface UserRow {
  isActive?: boolean;
  roleAssignments?: { role?: { key?: string } | null }[];
}

/** Roles that come with the tenant. A venue staffed only by these is unstaffed. */
const FOUNDING_ROLES = new Set(['TENANT_OWNER', 'PLATFORM_ADMIN']);

/** The Settings shell renders whichever tab the actor holds, so any of these. */
const SETTINGS_ROUTE = ['settings.view', 'users.view', 'roles.view', 'devices.view'];

const SCREENS: Record<ScreenId, { to: string; route: string[] }> = {
  /**
   * The TAB, not the shell. `/settings` with no segment redirects to the first
   * tab the actor may see, which is `spaces` for anyone holding `floor_map.view`
   * — so "create a branch" landed a brand-new owner on the floors-and-areas tab
   * with no branch to attach them to, which is the exact dead end this checklist
   * exists to prevent. The tab is a route segment (`settings/:tab`), and the ids
   * below are the ones `Settings.tsx` declares.
   */
  settingsBranches: { to: '/settings/branches', route: SETTINGS_ROUTE },
  settingsSpaces: { to: '/settings/spaces', route: SETTINGS_ROUTE },
  settingsStaff: { to: '/settings/staff', route: SETTINGS_ROUTE },
  catalogStations: { to: '/catalog', route: ['menu.view'] },
  catalogItems: { to: '/catalog', route: ['menu.view'] },
  catalogMenus: { to: '/catalog', route: ['menu.view'] },
  floorMapEditor: { to: '/floor-map/edit', route: ['floor_map.view'] },
};

/** The write key each gap is closed with, taken from the controller, not guessed. */
const FIX_PERMISSION: Record<StepId, string> = {
  branch: 'settings.update',
  floor: 'floor_map.create',
  area: 'floor_map.create',
  table: 'tables.create',
  station: 'kitchen.update',
  item: 'menu.create',
  menu: 'menu.create',
  map: 'floor_map.update',
  staff: 'users.create',
};

/** Anyone holding one of these can move the checklist forward. */
export const SETUP_FIX_PERMISSIONS = [...new Set(Object.values(FIX_PERMISSION))];

function statusOf(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : null;
}

async function read(probe: Probe): Promise<ProbeResult> {
  try {
    const { data } = await api.get(probe.url, { params: probe.params });
    const { rows, total } = rowsOf<unknown>(data);
    return { outcome: 'ok', rows, total };
  } catch (error) {
    // A 403 is "this role cannot look", which is a different answer from "not
    // done" — reporting it as a gap would send people to fix what is already there.
    return { outcome: statusOf(error) === 403 ? 'restricted' : 'error', rows: [], total: 0 };
  }
}

const UNREAD: ProbeResult = { outcome: 'restricted', rows: [], total: 0 };

function stateOf(result: ProbeResult, done: boolean): StepState {
  if (result.outcome === 'restricted') return 'restricted';
  if (result.outcome === 'error') return 'error';
  return done ? 'done' : 'missing';
}

export interface SetupStatus {
  steps: SetupStep[];
  loading: boolean;
  /** True once every step this actor can see is done. */
  complete: boolean;
  /** Steps that are missing *and* this actor can reach the screen that fixes them. */
  actionable: SetupStep[];
  reload: () => void;
}

export function useSetupStatus(): SetupStatus {
  const { can, canAny } = usePermission();
  const [results, setResults] = useState<Record<ProbeId, ProbeResult> | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);

    const ids = Object.keys(PROBES) as ProbeId[];
    Promise.all(
      // Skipping a probe the client already knows will 403 saves a request and
      // lands on the same conclusion the server's 403 would have produced.
      ids.map((id) => (can(PROBES[id].view) ? read(PROBES[id]) : Promise.resolve(UNREAD))),
    ).then((settled) => {
      if (!live) return;
      setResults(Object.fromEntries(ids.map((id, i) => [id, settled[i]])) as Record<ProbeId, ProbeResult>);
      setLoading(false);
    });

    return () => {
      live = false;
    };
  }, [can, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const steps = useMemo<SetupStep[]>(() => {
    if (!results) return [];

    const floors = results.floors.rows as FloorRow[];
    const drawn = floors.filter((floor) => (floor._count?.mapElements ?? 0) > 0);
    const published = (results.menus.rows as MenuRow[]).filter(
      // A menu nobody can order from is not published, whatever the flag says.
      (menu) => menu.isActive !== false && (menu._count?.items ?? 0) > 0,
    );
    const hired = (results.users.rows as UserRow[]).filter(
      (user) =>
        user.isActive !== false &&
        (user.roleAssignments ?? []).some(
          (assignment) => assignment.role?.key && !FOUNDING_ROLES.has(assignment.role.key),
        ),
    );

    /** Hand the editor the floor that is actually empty, not just the first one. */
    const emptyFloor = floors.find((floor) => !(floor._count?.mapElements ?? 0)) ?? null;

    const rows: { id: StepId; screen: ScreenId; probe: ProbeId; done: boolean; count: number; to?: string }[] = [
      { id: 'branch', screen: 'settingsBranches', probe: 'branches', done: results.branches.total > 0, count: results.branches.total },
      { id: 'floor', screen: 'settingsSpaces', probe: 'floors', done: results.floors.total > 0, count: results.floors.total },
      { id: 'area', screen: 'settingsSpaces', probe: 'areas', done: results.areas.total > 0, count: results.areas.total },
      { id: 'table', screen: 'settingsSpaces', probe: 'tables', done: results.tables.total > 0, count: results.tables.total },
      { id: 'station', screen: 'catalogStations', probe: 'stations', done: results.stations.total > 0, count: results.stations.total },
      { id: 'item', screen: 'catalogItems', probe: 'catalog', done: results.catalog.total > 0, count: results.catalog.total },
      { id: 'menu', screen: 'catalogMenus', probe: 'menus', done: published.length > 0, count: published.length },
      {
        id: 'map',
        screen: 'floorMapEditor',
        probe: 'floors',
        done: drawn.length > 0,
        count: drawn.length,
        to: emptyFloor ? `/floor-map/edit?floor=${emptyFloor.id}` : undefined,
      },
      { id: 'staff', screen: 'settingsStaff', probe: 'users', done: hired.length > 0, count: hired.length },
    ];

    return rows.map(({ id, screen, probe, done, count, to }) => {
      const target = SCREENS[screen];
      // Two gates, because either one alone still ends in a dead link: the write
      // key the API demands, and the route gate on the screen that holds it.
      const reachable = can(FIX_PERMISSION[id]) && canAny(...target.route);
      return {
        id,
        screen,
        count,
        state: stateOf(results[probe], done),
        to: reachable ? (to ?? target.to) : null,
        // Carried on the row rather than looked up by the caller, so a screen
        // that has to name the missing key cannot name a different one from the
        // key this row was actually derived from.
        needs: { view: PROBES[probe].view, fix: FIX_PERMISSION[id] },
      };
    });
  }, [results, can, canAny]);

  // Only steps that actually answered count towards "ready": an actor who can
  // read none of them would otherwise be told a venue they cannot see is fine.
  const answered = steps.filter((step) => step.state === 'done' || step.state === 'missing');
  const complete = answered.length > 0 && answered.every((step) => step.state === 'done');
  const actionable = steps.filter((step) => step.state === 'missing' && step.to);

  return { steps, loading, complete, actionable, reload };
}
